<template>
  <v-main>
    <OSimpleDialog ref="jsonDialog" :show-buttons="false" title="Debug">
      <pre class="pa-3 maxh-4" style="font-size: 12px">{{
        singleDetailRow
      }}</pre>
    </OSimpleDialog>

    <OSimpleDialog
      ref="detailsDialog"
      :show-buttons="false"
      title="Linhas com Erros"
      :maxWidth="1200"
    >
      <ODataGrid
        :data="detailGridData"
        @rowClicked="showRowDetails"
        rememberLastClick
      />
    </OSimpleDialog>

    <GenericReport
      ref="report"
      orientation="horizontal"
      hide-header
      class="clean-list"
      :reportSpec="reportSpec"
      :adapterSpec="adapterSpec"
      :fetcher="fetchData"
      rememberLastClick
      @rowClicked="showCCDetails"
      :limits="[10,50,100,200]"
    />
  </v-main>
</template>

<script>
import { go, CostCenter } from '@/models'
import moment from 'moment'
import ODataGrid from 'oderon-vue/cpts/ODataGrid.vue'
import GenericReport from 'oderon-vue/cpts/generic-report/GenericReport.vue'
import fmt from 'oderon-commons/src/formatters.js'
import OSimpleDialog from 'oderon-vue/cpts/dialogs/OSimpleDialog.vue'
import grid from 'oderon-commons/src/grid.js'
import { preProcessSpec } from 'oderon-commons/src/tables.js'

function valueAndCount(x) {
  return `${fmt.brDec(x.value)} (${x.count.toString().padStart(2, '0')})`
}

function hasError(x) {
  if (x.errors && x.errors.length > 0) return true
  if (x.install && x.install.errors && x.install.errors.length > 0) return true
  if (x.remove && x.remove.errors && x.remove.errors.length > 0) return true
  return false
}

export default {
  pageTitle: 'Saúde das Medições',
  components: {
    GenericReport,
    OSimpleDialog,
    ODataGrid
  },
  data() {
    return {
      allData: null,
      detailRows: [],
      detailGridData: null,
      singleDetailRow: null,

      reportSpec: {
        code: 'multiproductHealthReport',
        title: 'Saúde das Medições',

        // apesar de termos um fetcher, esta apiBase é usada
        // para montar o botão de colunas visíveis
        apiBase: '/multiproduct/technician',

        filters: [{ type: 'MultiproductPeriod', size: 'sm' }]
      },

      adapterSpec: {
        defCodes: [
          'id',
          'ccName',
          'install',
          'remove',
          'intervention',
          'proRata',
          'errors'
        ],
        defOrder: 'errors desc',
        defaultSelectable: true,
        columns: [
          { t: 'ID|', d: 'costCenter.id', color: '#ccc' },
          { t: 'Centro de Custo', code: 'ccName', d: 'costCenter.name' },
          { t: 'D. Ref.|', code: 'referenceDay', d: 'costCenter.referenceDay' },
          {
            t: 'Instal. (R$)|',
            code: 'installValue',
            d: 'install.value',
            f: 'brDec'
          },
          { t: 'Qtde.|', code: 'installCount', d: 'install.count' },
          {
            t: 'Instalações|',
            code: 'install',
            d: (row) => valueAndCount(row.install)
          },
          {
            t: 'Ret. (R$)|',
            code: 'removeValue',
            d: 'remove.value',
            f: 'brDec'
          },
          { t: 'Qtde.|', code: 'removeCount', d: 'remove.count' },
          {
            t: 'Retiradas|',
            code: 'remove',
            d: (row) => valueAndCount(row.remove)
          },
          {
            t: 'Interv. (R$)|',
            code: 'interventionValue',
            d: 'intervention.value',
            f: 'brDec'
          },
          { t: 'Qtde.|', code: 'interventionCount', d: 'intervention.count' },
          {
            t: 'Intervenções|',
            code: 'intervention',
            d: (row) => valueAndCount(row.intervention)
          },
          {
            t: 'Mensalidade (R$)|',
            code: 'proRataValue',
            d: 'proRata.value',
            f: 'brDec'
          },
          { t: 'Qtde.|', code: 'proRataCount', d: 'proRata.count' },
          {
            t: 'Mensalidades|',
            code: 'proRata',
            d: (row) => valueAndCount(row.proRata)
          },
          {
            t: 'Erros|',
            code: 'errors',
            d: (row) => row.errors.histories + row.errors.segments
          }
        ],
        rowStyleFn: ({ row }) => {
          if (row.errors.histories || row.errors.segments)
            return { bgColor: '#f00', color: '#fff' }
        }
      },

      detailSpec: preProcessSpec({
        idProp: 'uid',
        columns: [
          { t: 'Tipo', d: 'rowType' }, // mensalidade/serviço
          { t: 'Subtipo', d: 'type' }, // install/remove/intervention
          { t: 'Produto', d: 'productType.alias' },
          { t: 'Item', d: 'productItem.label' },
          { t: 'Placa', d: 'vehicle.code' },
          { t: 'CC', d: 'costCenter.name' },
          {
            t: 'Data/hora',
            d: (row) => row.timestamp || (row.install && row.install.timestamp),
            f: fmt.brDateOnly
          },
          { t: 'Inter. (dias)', d: 'intersectionDays' },
          { t: 'Pro-Rata', d: 'proRataPct', f: fmt.brDec },
          { t: 'Erros', d: (row) => row.errors && row.errors.join(', ') }
        ]
      })
    }
  },
  methods: {
    // Retorna os parâmetros para a API, mas não valida os obrigatórios.
    computeApiParams(filter) {
      let { technicianIds, vars } = filter
      if (technicianIds != null && technicianIds.length < 1)
        technicianIds = null

      // untilDate nunca é null (o DateRangePicker seta para hoje)
      let { fromDate, untilDate } = filter
      if (fromDate == null) fromDate = moment().startOf('month')

      // NB: Não envia limit e offset pois não queremos paginação
      let params = {
        technicianIds,
        vars,
        fromDate: moment(fromDate).format('YYYY-MM-DD'),
        untilDate: moment(untilDate).format('YYYY-MM-DD')
      }

      return params
    },

    async fetchData(apiParams) {
      let thisMonth = moment().format('YYYY-MM')
      let { fromDate, untilDate, strMonth } = apiParams.multiproductPeriod || {}

      let allCCs = await CostCenter.findAll({ includeInactive: false })
      allCCs = allCCs.filter((x) => x.name != 'CC Exemplo')

      let costCenterIds = allCCs.map((x) => x.id)

      let ps = { costCenterIds }

      if (fromDate != null) {
        ps.fromDate = fromDate
        ps.untilDate = untilDate
      } else {
        ps.month = strMonth || thisMonth
      }

      let res = await go.get('/multiproduct/cost-center/', ps)

      this.allData = res

      res.total.forEach((x) => (x.id = x.costCenter.id)) // para vue
      return res.total
    },

    showCCDetails({ row }) {
      let ccId = row.id

      let ccProRataRows = this.allData.proRataRows.filter(
        (x) => hasError(x) && x.costCenter && x.costCenter.id == ccId
      )

      let ccHistoryRows = this.allData.historyRows.filter(
        (x) => hasError(x) && x.costCenter && x.costCenter.id == ccId
      )

      this.detailRows = ccProRataRows.concat(ccHistoryRows)
      this.detailGridData = grid.generateWebGrid({
        rows: this.detailRows,
        spec: this.detailSpec
      })

      this.$refs.detailsDialog.open()
    },

    showRowDetails(row) {
      this.singleDetailRow = this.detailRows.find((x) => x.uid == row.id)
      this.$refs.jsonDialog.open()
    }
  }
}
</script>

<style></style>
